import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { ease } from "./Reveal";
import { scrollToId } from "./SmoothScroll";
import { HeroVisual } from "./HeroVisual";
import { StoreButtons } from "./StoreButtons";

const LINES = [
  [{ t: "Pulsa lebih," }],
  [{ t: "jadi " }, { t: "uang", c: "text-volt" }],
  [{ t: "beneran.", c: "text-outline" }],
];

const MaskLine = ({ parts, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "105%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease, delay: 0.15 + i * 0.12 }}
    >
      {parts.map((p) => (
        <span key={p.t} className={p.c}>{p.t}</span>
      ))}
    </motion.span>
  </span>
);

export const Hero = ({ rates, settings }) => {
  const { scrollY } = useScroll();
  const sceneY = useTransform(scrollY, [0, 800], [0, 120]);
  return (
    <section data-testid="hero-section" className="relative overflow-hidden pb-14 pt-24 md:pb-20 md:pt-28">
      <div className="hero-glow pointer-events-none absolute inset-0" />
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-40" />
      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-6 px-5 md:px-10 lg:grid-cols-12">
        <div className="order-2 lg:order-1 lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">
            <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-lime" /> Convert pulsa → saldo eWallet & bank
          </motion.p>
          <h1 data-testid="hero-title" className="font-display text-[2.9rem] font-bold leading-[0.95] tracking-[-0.04em] text-paper sm:text-6xl lg:text-[5.6rem]">
            {LINES.map((parts, i) => (
              <MaskLine key={i} parts={parts} i={i} />
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8, ease }} className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg">
            Punya pulsa lebih? Tukar jadi uang di BagiPulsa. Cair ke semua bank atau eWallet dalam <span className="text-paper">1–10 menit</span>, tanpa biaya admin.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease }} className="mt-10 flex flex-wrap gap-3">
            <button data-testid="hero-calc-button" onClick={() => scrollToId("kalkulator")} className="group flex items-center gap-2 rounded-full bg-volt px-7 py-4 font-semibold text-ink transition-transform duration-300 hover:scale-[1.04]">
              Hitung pulsa kamu
              <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </button>
            <StoreButtons settings={settings} variant="dark" prefix="hero" />
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="mt-12 flex flex-wrap gap-2" data-testid="hero-rate-chips">
            {rates.map((r) => (
              <span key={r.key} className="rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-1.5 font-mono text-xs text-zinc-300">
                {r.name} <span className="text-volt">{r.rate}%</span>
              </span>
            ))}
          </motion.div>
        </div>
        <motion.div style={{ y: sceneY }} className="order-1 h-[420px] sm:h-[500px] lg:order-2 lg:col-span-5 lg:h-[640px]" data-testid="hero-3d-scene">
          <HeroVisual />
        </motion.div>
      </div>
    </section>
  );
};
